import { useState } from 'react';
import { Lock, Sparkles, Check, Infinity as InfinityIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { PLAN_PRICES, PLAN_LIMITS, PRICING_URL, canCheckout } from '../lib/billing';
import { goToCheckout } from '../lib/billing';

// Everything to do with marking and selling a paid-plan feature lives here, so
// a locked feature looks and behaves the same wherever it appears. Add a new
// locked feature by using these two, never by restyling a padlock locally.

/**
 * Where the score would be, when there is no score to give.
 *
 * Deliberately NOT called locked, and it does not say locked either. A padlock
 * over a number reads as "we worked it out and are holding it back until you
 * pay", which is not what happens: past the free allowance nothing is sent to
 * the scoring engines at all, so the score was never produced. The icon stays,
 * because it is how the rest of the app marks a paid feature; the words tell
 * the truth about it.
 *
 * Same size and shape as ScoreCircle so the results screen does not jump about
 * between a scored attempt and an unscored one. They can still record, still
 * listen back, still practise.
 */
export const NoScore = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label="Pronunciation scoring is a paid feature"
        title="Pronunciation scoring is a paid feature"
        className="inline-flex flex-col items-center justify-center w-24 h-24 rounded-full mb-4
                   bg-gradient-to-br from-amber-300 to-amber-500 text-white shadow-sm
                   ring-1 ring-inset ring-amber-300 hover:brightness-105 active:scale-95 transition"
    >
        <Lock className="h-7 w-7" />
        <span className="text-[10px] font-semibold mt-0.5">No score</span>
    </button>
);

/**
 * The line under NoScore. "Upgrade to get" is the link, because that is the
 * action; the rest says what it gets them. Opens the same dialog as tapping
 * the padlock, so both routes land in one place.
 */
export const NoScoreCaption = ({ onUpgrade }) => (
    <p className="text-sm text-stone-500">
        <button type="button" onClick={onUpgrade}
                className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
            Upgrade to get
        </button>
        {' '}pronunciation scores and feedback.
    </p>
);

/** Gold padlock pill. Sits next to the label of a feature the plan can't use. */
export const ProLock = ({ label = 'Pro', className = '' }) => (
    <span
        title={`${label} feature`}
        className={`inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-300 whitespace-nowrap ${className}`}
    >
        <Lock className="h-2.5 w-2.5" />
        {label}
    </span>
);

/**
 * Free-text opinion box, shown only where the caller opts in with
 * `showOpinionBox` (the score-lock screen, where they have just hit the wall
 * and are still in the app to answer). Goes to the same inbox as the Help &
 * Feedback widget. Asked once: after a successful send it never shows again
 * for that person.
 */
const OpinionBox = () => {
    const { user } = useAuth();
    const storageKey = user?.id ? `opinion_sent_${user.id}` : null;
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(() => {
        try { return storageKey ? localStorage.getItem(storageKey) === '1' : false; } catch { return false; }
    });

    if (sent) {
        return <p className="text-center text-xs text-stone-400 pt-2">Thanks, that's really helpful.</p>;
    }

    const send = () => {
        if (!text.trim() || sending) return;
        setSending(true);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: text.trim(),
                email: user?.email,
                user_id: user?.id,
                page: 'free-allowance-used',
            }),
        })
            .then(() => {
                setSent(true);
                try { if (storageKey) localStorage.setItem(storageKey, '1'); } catch { /* noop */ }
            })
            .catch(() => { /* the box just stays put; they can try again */ })
            .finally(() => setSending(false));
    };

    return (
        <div className="pt-3 mt-1 border-t border-stone-100 space-y-2">
            <p className="text-sm font-semibold text-foreground">Before you go, one quick thing</p>
            <p className="text-xs text-stone-500">Tell me how you're finding the app so far, the good and the annoying? I read every one of these myself and would love your honest feedback.</p>
            <textarea
                className="w-full p-2.5 border border-stone-200 rounded-lg text-sm resize-none bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
                placeholder="Type away..."
                value={text}
                onChange={(e) => setText(e.target.value)}
            />
            <Button variant="outline" className="w-full" onClick={send} disabled={!text.trim() || sending}>
                {sending ? 'Sending...' : 'Send'}
            </Button>
        </div>
    );
};

/**
 * What a paid plan gives you. One list, shown by every upgrade prompt, so the
 * reasons to pay are written once and read the same everywhere. The numbers
 * come from PLAN_LIMITS, never typed into the copy.
 */
export const UpgradeBenefits = () => {
    const items = [
        <>
            <strong className="font-medium text-foreground">Natural French voices</strong>, spoken by real French
            speakers, instead of the standard robotic browser voice
        </>,
        <>
            <strong className="font-medium text-foreground">Word and sound level pronunciation feedback</strong> on
            every phrase you practise
        </>,
        <>
            <strong className="font-medium text-foreground">Collections of phrases generated for whatever you want
            to practise</strong>, tailored to your level, your gender and how formal you need to be
        </>,
        <>
            <strong className="font-medium text-foreground">{PLAN_LIMITS.pro.phrases.toLocaleString('en-GB')} phrases
            on Pro</strong>, or go Unlimited
        </>,
    ];

    return (
        <ul className="space-y-2 text-sm text-stone-600">
            {items.map((item, i) => (
                <li key={i} className="flex gap-2.5">
                    <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
};

/**
 * The two upgrade buttons, wherever upgrading is offered. One component so
 * they cannot drift apart: Pro in the app's green, Unlimited in the amber its
 * badge already uses, both solid, both with their icon and price. An outline
 * button next to a solid one read as the disabled option, which Unlimited is
 * not.
 */
export const UpgradeButtons = () => {
    const { user } = useAuth();

    // NOT Android in general. This is only true inside the app installed from
    // Google Play, whose Payments policy forbids sending people out to Stripe.
    // That app does not exist yet (PLAY_PACKAGE_ID is null in lib/platform.js),
    // so canCheckout() is true for everybody today, Android browsers included.
    if (!canCheckout()) {
        return (
            <p className="text-sm text-stone-600 leading-relaxed">
                To change your plan, open mimique.app in your browser and go to Settings.
            </p>
        );
    }

    return (
        <div className="space-y-2">
            <Button className="w-full" onClick={() => goToCheckout('pro', user?.email, user?.id)}>
                <Sparkles className="h-4 w-4" />
                Upgrade to Pro, {PLAN_PRICES.pro} a month
            </Button>
            <Button
                className="w-full bg-amber-500 text-white hover:bg-amber-600"
                onClick={() => goToCheckout('unlimited', user?.email, user?.id)}
            >
                <InfinityIcon className="h-4 w-4" />
                Go Unlimited, {PLAN_PRICES.unlimited} a month
            </Button>
        </div>
    );
};

/**
 * Upgrade prompt. `message` explains what they hit, in the words of whatever
 * blocked them — for a 402 that means passing the server's own `detail`, so the
 * cap numbers can never drift out of sync with the backend. Under it sits the
 * shared list of what the paid plans give them, because the thing they hit is
 * rarely the only reason to upgrade.
 *
 * No prices here on purpose: they live on the pricing page and in Stripe, and a
 * third copy would be one more place to forget to update.
 */
export const UpgradeDialog = ({ open, onOpenChange, title = 'This is a paid feature', message, showOpinionBox = false }) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader className="text-left">
                    <DialogTitle className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                            <Lock className="h-4 w-4" />
                        </span>
                        {title}
                    </DialogTitle>
                </DialogHeader>
                {message && <p className="text-sm leading-relaxed text-stone-600">{message}</p>}
                <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">What you get if you upgrade:</p>
                    <UpgradeBenefits />
                </div>
                {/* The price belongs on the button. Somebody deciding whether to
                    unlock a feature should not have to leave the dialog to find
                    out what it costs. */}
                <div className="pt-1">
                    <UpgradeButtons />
                </div>
                {/* New tab on purpose: comparing the plans should not cost them
                    the page they were on. */}
                <a href={PRICING_URL} target="_blank" rel="noopener noreferrer"
                   className="block text-center text-xs text-primary underline underline-offset-2">
                    Compare what each plan includes
                </a>
                {/* The line that answers the question somebody actually has at
                    this point. How the checkout is prefilled is our problem, not
                    theirs. The wording matches the pricing table on the landing
                    page, so the promise does not change between the two. */}
                <p className="text-center text-xs text-stone-400">
                    Billed monthly. Cancel any time.
                </p>
                {showOpinionBox && <OpinionBox />}
            </DialogContent>
        </Dialog>
    );
};
